import Client from '../Client.provider';
import {
  iCrmCompany,
  iCrmCompanyForm,
  iCrmContact,
  iCrmContactForm,
  iProspectFile,
  iProspectFileForm,
  iInteraction,
  iInteractionForm,
  iSiretLookupResult,
  iCrmDashboardStats,
  eCrmProspectStatus,
} from './Crm.type';

export interface iCrmRepository {
  // Companies
  getCompanies: () => Promise<iCrmCompany[]>;
  getCompany: (id: number) => Promise<iCrmCompany>;
  createCompany: (data: iCrmCompanyForm) => Promise<iCrmCompany>;
  updateCompany: (id: number, data: iCrmCompanyForm) => Promise<iCrmCompany>;
  deleteCompany: (id: number) => Promise<{ message: string }>;
  siretLookup: (siret: string) => Promise<iSiretLookupResult>;

  // Contacts
  getContactsByCompany: (companyId: number) => Promise<iCrmContact[]>;
  createContact: (data: iCrmContactForm) => Promise<iCrmContact>;
  updateContact: (id: number, data: iCrmContactForm) => Promise<iCrmContact>;
  deleteContact: (id: number) => Promise<{ message: string }>;

  // Prospect Files
  getProspectsByEvent: (eventId: number) => Promise<iProspectFile[]>;
  getProspectFile: (id: number) => Promise<iProspectFile>;
  createProspectFile: (data: iProspectFileForm) => Promise<iProspectFile>;
  updateProspectFile: (
    id: number,
    data: iProspectFileForm
  ) => Promise<iProspectFile>;
  updateProspectStatus: (
    id: number,
    status: eCrmProspectStatus
  ) => Promise<iProspectFile>;
  deleteProspectFile: (id: number) => Promise<{ message: string }>;

  // Interactions
  getInteractions: (prospectFileId: number) => Promise<iInteraction[]>;
  createInteraction: (data: iInteractionForm) => Promise<iInteraction>;
  deleteInteraction: (id: number) => Promise<{ message: string }>;

  // Dashboard
  getDashboardStats: (eventId: number) => Promise<iCrmDashboardStats>;

  // Convert
  convertToExhibitor: (
    id: number
  ) => Promise<{ message: string; prospect: iProspectFile }>;
}

export class CrmRepository extends Client implements iCrmRepository {
  // ── Companies ──────────────────────────────────────────────

  public async getCompanies(): Promise<iCrmCompany[]> {
    return await this.restricted()
      .get<iCrmCompany[]>('/crm/companies')
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async getCompany(id: number): Promise<iCrmCompany> {
    return await this.restricted()
      .get<iCrmCompany>(`/crm/companies/${id}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async createCompany(data: iCrmCompanyForm): Promise<iCrmCompany> {
    return await this.restricted()
      .post<iCrmCompany>('/crm/companies', data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async updateCompany(
    id: number,
    data: iCrmCompanyForm
  ): Promise<iCrmCompany> {
    return await this.restricted()
      .put<iCrmCompany>(`/crm/companies/${id}`, data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async deleteCompany(id: number): Promise<{ message: string }> {
    return await this.restricted()
      .delete(`/crm/companies/${id}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async siretLookup(siret: string): Promise<iSiretLookupResult> {
    return await this.restricted()
      .get<iSiretLookupResult>(`/crm/companies/siret-lookup/${siret}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  // ── Contacts ───────────────────────────────────────────────

  public async getContactsByCompany(companyId: number): Promise<iCrmContact[]> {
    return await this.restricted()
      .get<iCrmContact[]>(`/crm/companies/${companyId}/contacts`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async createContact(data: iCrmContactForm): Promise<iCrmContact> {
    return await this.restricted()
      .post<iCrmContact>('/crm/contacts', data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async updateContact(
    id: number,
    data: iCrmContactForm
  ): Promise<iCrmContact> {
    return await this.restricted()
      .put<iCrmContact>(`/crm/contacts/${id}`, data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async deleteContact(id: number): Promise<{ message: string }> {
    return await this.restricted()
      .delete(`/crm/contacts/${id}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  // ── Prospect Files ─────────────────────────────────────────

  public async getProspectsByEvent(eventId: number): Promise<iProspectFile[]> {
    return await this.restricted()
      .get<iProspectFile[]>(`/crm/events/${eventId}/prospects`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async getProspectFile(id: number): Promise<iProspectFile> {
    return await this.restricted()
      .get<iProspectFile>(`/crm/prospect-files/${id}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async createProspectFile(
    data: iProspectFileForm
  ): Promise<iProspectFile> {
    return await this.restricted()
      .post<iProspectFile>('/crm/prospect-files', data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async updateProspectFile(
    id: number,
    data: iProspectFileForm
  ): Promise<iProspectFile> {
    return await this.restricted()
      .put<iProspectFile>(`/crm/prospect-files/${id}`, data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async updateProspectStatus(
    id: number,
    status: eCrmProspectStatus
  ): Promise<iProspectFile> {
    return await this.restricted()
      .put<iProspectFile>(`/crm/prospect-files/${id}/status`, { status })
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async deleteProspectFile(id: number): Promise<{ message: string }> {
    return await this.restricted()
      .delete(`/crm/prospect-files/${id}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  // ── Interactions ───────────────────────────────────────────

  public async getInteractions(
    prospectFileId: number
  ): Promise<iInteraction[]> {
    return await this.restricted()
      .get<iInteraction[]>(`/crm/prospect-files/${prospectFileId}/interactions`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async createInteraction(
    data: iInteractionForm
  ): Promise<iInteraction> {
    return await this.restricted()
      .post<iInteraction>('/crm/interactions', data)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async deleteInteraction(id: number): Promise<{ message: string }> {
    return await this.restricted()
      .delete(`/crm/interactions/${id}`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  // ── Dashboard ────────────────────────────────────────────

  public async convertToExhibitor(
    id: number
  ): Promise<{ message: string; prospect: iProspectFile }> {
    return await this.restricted()
      .post(`/crm/prospect-files/${id}/convert`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ??
            `${error.response?.status} | Unknown error...`
        );
      });
  }

  public async getDashboardStats(eventId: number): Promise<iCrmDashboardStats> {
    return await this.restricted()
      .get<iCrmDashboardStats>(`/crm/events/${eventId}/dashboard-stats`)
      .then((e) => e.data)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            `${error.response?.status} | Unknown error...`
        );
      });
  }
}
